import { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { st } from "../../styles.js";
import { Icon } from "../../icons";
import { Glyph, FPill } from "../../components/ui";
import { BuzerIcon } from "../../BuzerIcons";
import { Button, Input, Caption, Segmented, List, ListRow, Lead, LeadWithStars, Tag, EmptyState } from "../../components/ui/ds";
import { iconChipStyle } from "../../lib/icons-resolve";
import { LIFE_CATS, LIFE_CAT } from "../../lib/constants";
import { DIFFICULTIES, DIFF_SK } from "../../lib/points";
import {
  CHAL_FILTER_DEFAULTS, chalDifficulty, facetCount, facetExists, filterChallenges,
  hasFilter, isNarrowed, sortChallenges,
} from "../../lib/challenge-filter";
import { joinedLabel } from "../../lib/format";
import { ChallengeInfoModal } from "./ChallengeInfoModal";
import "./challenges-filter.css";

// Prebiehajúce výzvy — JEDEN zoznam, nad ním hľadanie + filtre.
//
// Predtým (v13 C1/C2) tu boli DVA zoznamy: „Oficiálne výzvy" zoskupené po kategóriách a pod nimi
// „Prebiehajúce" so svojím vlastným radením a dvomi pilulkami. Tá istá výzva sa teda vykresľovala
// dvakrát v dvoch rôznych štýloch a filtre boli rozdelené medzi dve miesta. Zadanie majiteľky
// (2026-08-05): „nech je tam len jeden zoznam ktorý sa dá pekne filtrovať". Oficiálne výzvy sa
// nestratili — nesú štítok „Oficiálna" a dajú sa vyfiltrovať cez filter Pôvod.
//
// Filtre sú rozdelené na dve úrovne, aby sa na 360 px nezišlo priveľa ovládačov naraz:
//   · vždy viditeľné = hľadanie + zapojenie (Všetky · Zapojené · Nezapojené) — najčastejšia otázka,
//   · v popoveri pod lievikom = radenie, typ, pôvod, kategória, náročnosť.
// Aktívne filtre sú POMENOVANÉ priamo na obrazovke (zhoditeľné pilulky + „Zrušiť filtre"), takže
// nikdy nevznikne prázdny zoznam bez vysvetlenia.

// Predikáty, fasetové počty a radenie žijú v `lib/challenge-filter.ts` (a majú tam testy) —
// tu ostáva len obrazovka.
const FILTER_MIN = 5;  // pod týmto počtom výziev je filtračná lišta len clutter — všetko aj tak vidíš

export function ChallengesTab({ reload, embedded = false, me, lite = false }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState(CHAL_FILTER_DEFAULTS);  // stav všetkých filtrov v jednom objekte
  const [sort, setSort] = useState("popular");     // radenie beží na klientovi — bez ďalšieho fetchu
  const [filterOpen, setFilterOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [detail, setDetail] = useState(null);      // v24 — cid → detail modal
  const load = useCallback(async () => {
    try { setData(await api("/api/challenges/public")); }
    catch { setData({ challenges: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  async function join(cid) { setBusy(cid); try { await api("/api/challenges/" + cid + "/join", { method: "POST" }); await load(); reload(); } catch (e) { alert(e.message); } setBusy(null); }
  if (!data) return <div style={{ ...st.empty, marginTop: 40 }}><div className="spin" /></div>;

  const all = data.challenges || [];
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const reset = () => setF(CHAL_FILTER_DEFAULTS);
  const shown = sortChallenges(filterChallenges(all, f), sort);
  const countFor = (facet, v) => facetCount(all, f, facet, v);
  // má zmysel pilulku vôbec ponúknuť? (existuje taká výzva bez ohľadu na ostatné filtre)
  const exists = (facet, v) => facetExists(all, facet, v);

  const narrowed = isNarrowed(f);
  const filterOn = hasFilter(f);
  // pomenované aktívne filtre — každý sa dá zhodiť ťuknutím
  const chips = [
    f.join !== "all" && ["join", f.join === "in" ? "Zapojené" : "Nezapojené"],
    f.type !== "all" && ["type", f.type === "comp" ? "Súťaže" : "Výzvy"],
    f.origin !== "all" && ["origin", f.origin === "official" ? "Oficiálne" : "Od ľudí"],
    f.cat !== "all" && ["cat", (LIFE_CAT[f.cat] || {}).label || f.cat],
    f.diff !== "all" && ["diff", DIFF_SK[f.diff]],
  ].filter(Boolean);

  // skupina pilulák do popoveru: options = [[hodnota, popis], …]
  function group(facet, label, options) {
    return (
      <>
        {/* rovnaká menovka skupiny filtrov ako v Organizéri (PlanTab) — Caption berie style, nie triedu */}
        <Caption style={{ display: "block", margin: "0 0 9px" }}>{label}</Caption>
        <div className="bz-chalf__grp">
          {options.map(([v, lbl]) => (
            <FPill key={v} active={f[facet] === v} onClick={() => set(facet, v)} label={lbl}
              count={v === "all" ? undefined : countFor(facet, v)} />
          ))}
        </div>
      </>
    );
  }

  // v33 — riadok výzvy v ROVNAKOM štýle ako zoznam návykov (List/ListRow/Lead primitívy);
  // patrí dovnútra <List> (spoločný rám + hairline oddeľovače).
  function row(c) {
    const lc = c.life_cat ? LIFE_CAT[c.life_cat] : null;
    const d = chalDifficulty(c);
    const meta = (
      <span className="bz-chalf__meta">
        {/* oficiálne výzvy sa v jednom zozname nesmú stratiť — štítok vo farbe témy (zadanie majiteľky) */}
        {!!c.is_official && <Tag tone="brand" size="xs" icon="medal">Oficiálna</Tag>}
        {!!c.is_competition && <Tag tone="neutral" size="xs" icon="trophy">Súťaž</Tag>}
        {/* +body sa v miniatúre neukazuje (zadanie majiteľky); náročnosť je hviezdami POD ikonou */}
        {lc && <Tag tone="neutral" size="xs" icon={lc.icon}>{lc.label}</Tag>}
        <span className="bz-chalf__ppl"><Icon name="friends" size={12} color="currentColor" /> {c.participants}</span>
      </span>
    );
    // feat/challenge-visibility — uzavretá VEREJNÁ výzva (join_mode==='request'): vstup ide cez
    // žiadosť, ktorú schvaľuje zakladateľ; kým čaká, riadok ukazuje „Čaká na schválenie".
    const trailing = c.joined
      ? <span className="bz-chalf__state bz-chalf__state--in"><Icon name="check" size={14} color="var(--accent)" stroke={3} /> {joinedLabel({ lower: true })}</span>
      : c.requested
        ? <span className="bz-chalf__state"><Icon name="hourglass" size={13} color="currentColor" /> Čaká na schválenie</span>
        : <Button variant="secondary" size="sm" onClick={(e) => { if (e) e.stopPropagation(); join(c.id); }} disabled={busy === c.id} style={{ flexShrink: 0 }}>
            {busy === c.id ? "…" : (c.join_mode === "request" ? "Požiadať" : "Zapojiť sa")}
          </Button>;
    // hviezdy náročnosti POD ikonou — ten istý primitív ako riadok návyku v Dnes
    // (LeadWithStars), aby výzva/súťaž vyzerala konzistentne; v Lite sa hviezdy nekreslia
    return (
      <ListRow key={c.id} onClick={() => setDetail(c.id)}
        lead={<LeadWithStars level={lite ? null : d} active={!!c.joined} tone="outline" size="sm"
          style={{ ...iconChipStyle(c.icon_color), borderColor: "var(--neutral-border-strong)" }}>
          <Glyph icon={c.icon} size={17} fallback="challenge" /></LeadWithStars>}
        title={c.name} meta={meta} trailing={trailing} />
    );
  }

  return (
    <>
      {!embedded && <div style={st.heroHead}><h1 style={st.heroTitle}>Prebiehajúce výzvy.</h1></div>}
      <div style={{ ...st.taskDesc, ...(embedded ? { marginTop: 4 } : {}) }}>Nájdi si výzvu alebo súťaž, ktorá ti sadne. Plň ju denne a stúpaj v rebríčku.</div>

      {all.length > FILTER_MIN && (<>
        {/* hľadanie cez plnú šírku, pod ním samostatný riadok s tlačidlom Filter (zadanie majiteľky) */}
        <div className="bz-chalf__search">
          <span className="bz-chalf__mag"><BuzerIcon name="search" size={16} color="var(--neutral-text-subtle)" /></span>
          <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Hľadať výzvu" aria-label="Hľadať výzvu" />
          {f.q && (
            <button type="button" className="bz-chalf__clear" onClick={() => set("q", "")} aria-label="Vymazať hľadanie">
              <BuzerIcon name="close" size={16} color="currentColor" />
            </button>
          )}
        </div>
        <div className="bz-chalf">
          <Button variant={filterOn ? "primary" : "secondary"} size="sm" className="bz-chalf__filterbtn"
            onClick={() => setFilterOpen((o) => !o)} aria-label="Filtre a radenie">
            <BuzerIcon name="filter" size={15} color="currentColor" /> Filter
          </Button>
          {filterOpen && (<>
            <div className="bz-chalf__scrim" onClick={() => setFilterOpen(false)} />
            <div className="bz-chalf__pop">
              <Caption style={{ display: "block", margin: "0 0 9px" }}>Radenie</Caption>
              <div className="bz-chalf__sort">
                <Segmented className="bz-seg--quiet bz-seg--block" value={sort} onChange={setSort}
                  options={[{ value: "popular", label: "Populárne" }, { value: "new", label: "Najnovšie" }]} />
              </div>
              {/* zapojenie („v čom už som?") je teraz priamo vo filtri, nie ako samostatný riadok */}
              {group("join", "Zapojenie", [["all", "Všetky"], ["in", "Zapojené"], ["out", "Nezapojené"]])}
              {exists("type", "comp") && group("type", "Typ", [["all", "Všetko"], ["chal", "Výzvy"], ["comp", "Súťaže"]])}
              {exists("origin", "official") && exists("origin", "community")
                && group("origin", "Pôvod", [["all", "Všetko"], ["official", "Oficiálne"], ["community", "Od ľudí"]])}
              {group("cat", "Kategória", [["all", "Všetky"], ...LIFE_CATS.filter((c) => exists("cat", c.key)).map((c) => [c.key, c.label])])}
              {/* v Lite sa náročnosť ani body nikde nezobrazujú (§8.3) — filtrovať podľa nich by nedávalo zmysel */}
              {!lite && group("diff", "Náročnosť", [["all", "Všetky"], ...DIFFICULTIES.filter((d) => exists("diff", d)).map((d) => [d, DIFF_SK[d]])])}
              <Button variant="secondary" block disabled={!narrowed} onClick={() => { reset(); setFilterOpen(false); }}>Zrušiť filtre</Button>
            </div>
          </>)}
        </div>

        {/* aktívne filtre POMENOVANÉ na obrazovke — každý sa zhodí ťuknutím, celé naraz tlačidlom.
            Hľadanie tu chip nemá: text vidno priamo v poli a má vlastné ✕. */}
        {narrowed && (
          <div className="bz-chalf__active">
            {chips.map(([k, lbl]) => (
              <FPill key={k} active label={<>{lbl} <BuzerIcon name="close" size={11} color="currentColor" /></>} onClick={() => set(k, CHAL_FILTER_DEFAULTS[k])} />
            ))}
            <Button variant="ghost" size="sm" onClick={reset}>Zrušiť filtre</Button>
          </div>
        )}
      </>)}

      {all.length === 0 ? (
        <EmptyState icon="flag" title="Zatiaľ žiadne výzvy"
          desc="Verejné výzvy a súťaže sa tu objavia hneď, ako nejaká vznikne. Môžeš byť prvý/á — založ ju tlačidlom + v spodnej lište." />
      ) : shown.length === 0 ? (
        <EmptyState icon="search" title="Nič sa nenašlo"
          desc={f.q.trim()
            ? "Žiadna výzva sa nevolá podobne. Skús iné slovo alebo uvoľni filtre."
            : "Takúto kombináciu filtrov zatiaľ nespĺňa žiadna výzva."}>
          <Button variant="secondary" block onClick={reset}>Zrušiť filtre</Button>
        </EmptyState>
      ) : (
        <div className="bz-chalf__list">{shown.map((c) => <List key={c.id}>{row(c)}</List>)}</div>
      )}

      {detail != null && <ChallengeInfoModal cid={detail} me={me} onClose={() => setDetail(null)}
        onJoin={async (id) => { await join(id); setDetail(null); }} joinBusy={busy === detail} />}
    </>
  );
}
